import "./Login.css";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiPost } from "./apiClient";

const Login = ({ setUser }) => {
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [isRegistering, setIsRegistering] = useState(false);
    const navigate = useNavigate();
    const [showPassword, setShowPassword] = useState(false);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const checkPasswordStrength = (pwd) => {
        return {
          length: pwd.length >= 8,
          uppercase: /[A-Z]/.test(pwd),
          lowercase: /[a-z]/.test(pwd),
          number: /\d/.test(pwd),
          symbol: /[!@#$%^&*(),.?":{}|<>]/.test(pwd),
        };
    };

    const isStrongPassword = () => {
        const strength = checkPasswordStrength(password);
        return Object.values(strength).every(Boolean);
    };

    const formatPhoneNumber = (p) => {
        const cleaned = p.replace(/\D/g, "");
        if (cleaned.length <= 4) return cleaned;
        if (cleaned.length <= 7) return cleaned.replace(/(\d{4})(\d{1,3})/, "$1 $2");
        return cleaned.replace(/(\d{4})(\d{3})(\d{1,4})/, "$1 $2 $3");
    };

    const isPhoneValid = (p) => {
        const cleaned = p.replace(/\D/g, "");
        return /^09\d{9}$/.test(cleaned);
    };

    const checkUsernameValidity = (username) => {
        return {
            length: username.length >= 3 && username.length <= 20,
            alphanumeric: /^[a-zA-Z0-9_-]+$/.test(username),
        };
    };

    const isUsernameValid = (username) => {
        const validity = checkUsernameValidity(username);
        return Object.values(validity).every(Boolean);
    };

    const handleGoogleLogin = () => {
        const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
        window.location.href = `${apiUrl}/auth/google`;
    };

    // Handle User Registration
    const handleRegister = async () => {
        if (!acceptedTerms) {
            alert("برای ثبت‌نام باید با قوانین و شرایط موافقت کنید.");
            return;
        }

        if (!username.trim() || !password.trim()) {
            alert("لطفاً نام کاربری و رمز عبور را وارد کنید.");
            return;
        }

        const normalizedUsername = username.trim().toLowerCase();

        if (!isUsernameValid(normalizedUsername)) {
            alert("نام کاربری باید بین 3 تا 20 کاراکتر باشد و فقط شامل حروف و اعداد انگلیسی، زیرخط (_) یا خط تیره (-) باشد.");
            return;
        }

        if (!isStrongPassword()) {
            alert("رمز عبور شما به اندازه کافی قوی نیست.");
            return;
        }

        if (!isPhoneValid(phone)) {
            alert("شماره موبایل معتبر نیست. مثال: 6789 345 0912");
            return;
        }

        try {
            const data = await apiPost("/auth/register", {
                username: normalizedUsername,
                password,
                phone,
                accepted_terms: acceptedTerms,
            });

            localStorage.setItem("token", data.token);
            localStorage.setItem("user", data.user_id);
            setUser(data.user_id);
            navigate("/chat");
        } catch (error) {
            if (error.message === "Username already taken") {
                alert("نام کاربری قبلاً انتخاب شده است. لطفاً نام دیگری وارد کنید.");
            } else {
                alert("❌ خطا در ثبت نام! لطفاً دوباره امتحان کنید.");
            }
            console.error("Error registering:", error.message);
        }
    };

    // Handle User Login
    const handleLogin = async () => {
        if (!username.trim() || !password.trim()) {
            alert("نام کاربری و رمز عبور را وارد کنید.");
            return;
        }

        const normalizedUsername = username.trim().toLowerCase();

        try {
            const data = await apiPost("/auth/login", {
                username: normalizedUsername,
                password,
            });

            localStorage.setItem("token", data.token);
            localStorage.setItem("user", data.user_id);
            setUser(data.user_id);
            navigate("/chat");
        } catch (error) {
            if (error.message === "User not found") {
                alert("نام کاربری یافت نشد.");
            } else if (error.message === "Wrong password") {
                alert("رمز عبور نادرست است.");
            } else {
                alert("❌ خطا در ورود! لطفاً دوباره امتحان کنید.");
            }
            console.error("Error logging in:", error.message);
        }
    };

    return (
        <div className="login-page">
            <div className="login-container">
                <div className="intro">
                    <h1>👋 خوش آمدید!</h1>
                    <p>اطلاعات دقیق درباره مواد ممنوعه و قوانین دوپینگ</p>
                </div>

                <h2>{isRegistering ? "ثبت نام" : "ورود"}</h2>

                <div className="input-group">
                    <input
                        type="text"
                        placeholder="نام کاربری"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        style={{ direction: "ltr" }}
                    />
                </div>

                {isRegistering && (
                    <div className="input-group">
                        <input
                            type="tel"
                            placeholder="شماره تلفن همراه (مثال: 6789 345 0912)"
                            value={phone}
                            onChange={(e) => setPhone(formatPhoneNumber(e.target.value))}
                        />
                    </div>
                )}

                <div className="input-group password-input-group">
                    <input
                        type={showPassword ? "text" : "password"}
                        placeholder="رمز عبور"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        style={{ direction: "ltr" }}
                    />
                    <span
                        className="toggle-password"
                        onClick={() => setShowPassword((prev) => !prev)}
                    >
                        {showPassword ? "🔒" : "🔓"}
                    </span>
                </div>

                {isRegistering && password && (
                    <ul className="password-rules">
                        {Object.entries(checkPasswordStrength(password)).map(([rule, passed]) => (
                            <li key={rule} className={passed ? "pass" : "fail"}>
                                {passed ? "✅" : "❌"}{" "}
                                {rule === "length" && "حداقل ۸ کاراکتر"}
                                {rule === "uppercase" && "حداقل یک حرف بزرگ انگلیسی (A-Z)"}
                                {rule === "lowercase" && "حداقل یک حرف کوچک انگلیسی (a-z)"}
                                {rule === "number" && "حداقل یک عدد انگلیسی"}
                                {rule === "symbol" && "حداقل یک نماد (!@#$...)"}
                            </li>
                        ))}
                    </ul>
                )}

                {isRegistering && (
                    <div className="terms-checkbox">
                        <input
                            type="checkbox"
                            id="acceptTerms"
                            checked={acceptedTerms}
                            onChange={(e) => setAcceptedTerms(e.target.checked)}
                        />
                        <label htmlFor="acceptTerms">
                            با <a href="/terms" target="_blank" rel="noopener noreferrer">قوانین و شرایط</a> موافقت می‌کنم.
                        </label>
                    </div>
                )}

                <button
                    onClick={isRegistering ? handleRegister : handleLogin}
                    className="login-button"
                >
                    {isRegistering ? "ثبت نام" : "ورود"}
                </button>

                <button onClick={handleGoogleLogin} className="google-login-button">
                    <img
                        src="./images/google.png"
                        alt="Google logo"
                        style={{ width: 20, height: 20, marginLeft: 12 }}
                    />
                    {isRegistering ? "ثبت نام با حساب گوگل" : "ورود با حساب گوگل"}
                </button>

                <p className="toggle-link" onClick={() => setIsRegistering(!isRegistering)}>
                    {isRegistering ? "حساب دارید؟ وارد شوید" : "حساب ندارید؟ ثبت نام کنید"}
                </p>
            </div>
        </div>
    );
};

export default Login;
